/**
 * 作り直しの監査 2 本の入出力スキーマ（API §4.0 の `getImplementationCoverage` /
 * `getDanglingReferences`）。
 *
 * 今の 2 本（`spec-graph-audit.ts`）と変わるのは 2 点だけである。
 *
 * 1. **単位が操作から処理と業務へ替わる。** 今は 7 分類がすべて操作を母集団にしているが、操作の
 *    型そのものが無くなる。母集団が操作だった 4 分類（テストの無い操作 / 画面の無い操作 /
 *    骨格だけの操作 / データの宣言なし）が落ち、「処理を持たない業務」と「実装を持たない処理」が
 *    入る（7 − 4 ＋ 2 ＝ 5 分類）。実装待ちと実装の宙吊りは母集団が実装の宣言なので残り、
 *    宛先だけが操作から処理に替わる
 * 2. **ID が number になる。** 参照の突き合わせに使う列は、今はパス形の文字列である
 *
 * **名前が同じまま中身が替わるので、新しいスキーマは別のモジュールに置く。** 契約
 * （`orpc-contract.ts`）への配線は切替の回にまとめて行う。ここに置いただけでは今の procedure の
 * 挙動は 1 つも変わらない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 作り直しの監査 2 クエリ（実装カバレッジ・宙吊り参照）の入出力を、処理と業務の単位・number の ID で型に固定する
 * @graph-connects none
 */
import { z } from 'zod';
import { ImplementationKindSchema, IsoDateTimeSchema, LocaleInputShape, NonEmptyStringSchema, SpecEdgeTypeSchema, SpecElementIdSchema, UntranslatedSchema, } from './spec-graph-common.js';
// =============================================================================
// getImplementationCoverage（作り直し）— 処理と業務の単位で残りを返す
// =============================================================================
/**
 * 入力。ドメインの番号で絞れる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 作り直しの実装カバレッジ監査の入力（ドメインの番号と表示言語）
 * @graph-connects none
 */
export const GetElementImplementationCoverageInputSchema = z.object({
    domain: SpecElementIdSchema.optional(),
    ...LocaleInputShape,
});
/**
 * 実装待ちの処理 1 件（宣言先が SPG に実在しない）。
 *
 * 宛先が操作から処理に替わったので、業務は単数の列ではなく**逆引きの並び**になる。1 つの処理は
 * 複数の業務の並びに載りうる（同じ文の規則が 23 本あり、最大 19 業務に写っていた）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 実装先が SPG に実在しない処理 1 件（処理の番号・表示名・使っている業務の逆引き・宣言先）
 * @graph-connects none
 */
export const ElementImplementationWaitingRowSchema = z.object({
    ruleId: SpecElementIdSchema,
    label: z.string(),
    untranslated: UntranslatedSchema,
    usedByBusinessIds: z.array(SpecElementIdSchema),
    kind: ImplementationKindSchema,
    ref: NonEmptyStringSchema,
});
/**
 * 宙吊りの処理 1 件（宣言先が SPG から消えた）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 宣言先が SPG から消えた処理 1 件（実装待ちの行に、最後に見えた時刻と消した人を足す）
 * @graph-connects none
 */
export const ElementDanglingImplementationRowSchema = ElementImplementationWaitingRowSchema.extend({
    lastSeenAt: IsoDateTimeSchema.nullable(),
    removedBy: NonEmptyStringSchema.nullable(),
});
/**
 * 実装を 1 件も持たない処理 1 件（制約 46。割り当ての残りの計数）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 実装を 1 件も持たない処理 1 件（処理の番号・表示名・使っている業務の逆引き）
 * @graph-connects none
 */
export const ElementRuleWithoutImplementationRowSchema = z.object({
    ruleId: SpecElementIdSchema,
    label: z.string(),
    untranslated: UntranslatedSchema,
    usedByBusinessIds: z.array(SpecElementIdSchema),
});
/**
 * 処理の並びが空の業務 1 件（復元の残りの計数）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 処理の並びが空の業務 1 件（業務の番号・表示名・ドメインの番号）
 * @graph-connects none
 */
export const ElementBusinessWithoutRulesRowSchema = z.object({
    businessId: SpecElementIdSchema,
    label: z.string(),
    untranslated: UntranslatedSchema,
    domain: SpecElementIdSchema.nullable(),
});
/**
 * 仕様に無い実装 1 件（SPG に在るがどの処理からも指されない Api / Task）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business どの処理からも指されていない境界 1 件（修飾名・種別・所属リポジトリ）
 * @graph-connects none
 */
export const ElementSpecLessImplementationRowSchema = z.object({
    qualifiedName: NonEmptyStringSchema,
    kind: ImplementationKindSchema,
    repository: NonEmptyStringSchema,
});
/**
 * 出力。5 分類の残りをそのまま返す。
 *
 * 今の 7 分類のうち母集団が操作だった 4 分類（テストの無い操作 / 画面の無い操作 / 骨格だけの
 * 操作 / データの宣言なし）が落ち、「処理を持たない業務」と「実装を持たない処理」が入るので
 * 7 − 4 ＋ 2 ＝ 5 である。**実装の宙吊りは残る** — 母集団が操作ではなく実装の宣言そのもので、
 * 設計 §4.0 も残すと言っている。`getDanglingReferences` にも同じ行が出るが、あちらはグラフ内の
 * 宙吊りと孤立した用語を並べる日次検査で、こちらは実装カバレッジの 1 分類である。
 *
 * 件数の要約は持たない。各分類の並びそのものが答えで、要約を並べると同じ事実の 2 表現になる。
 * 母数としてしか得られない業務と処理の総数だけを別に返す。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 作り直しの実装カバレッジ監査の出力（5 分類の残りと、業務と処理の母数）
 * @graph-connects none
 */
export const GetElementImplementationCoverageOutputSchema = z.object({
    generatedAt: IsoDateTimeSchema,
    domainFilter: SpecElementIdSchema.nullable(),
    businessTotal: z.number().int().min(0),
    ruleTotal: z.number().int().min(0),
    implementationWaiting: z.array(ElementImplementationWaitingRowSchema),
    danglingImplementation: z.array(ElementDanglingImplementationRowSchema),
    businessWithoutRule: z.array(ElementBusinessWithoutRulesRowSchema),
    ruleWithoutImplementation: z.array(ElementRuleWithoutImplementationRowSchema),
    specLessImplementation: z.array(ElementSpecLessImplementationRowSchema),
});
// =============================================================================
// getDanglingReferences（作り直し）— ID が number になる
// =============================================================================
/**
 * 入力。絞り込みは無く、表示言語だけを受ける。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 作り直しの宙吊り参照監査の入力（表示言語だけ）
 * @graph-connects none
 */
export const GetElementDanglingReferencesInputSchema = z.object({ ...LocaleInputShape });
/**
 * 削除済みノードを指したままのエッジ 1 本。
 *
 * **エッジの ID の列を持たない。** 作り直しのエッジは宣言の射影で id を持たず、同一性は
 * （型, 起点, 終点）の 3 つ組である（NE §1 ID）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 削除済みノードを指したままのエッジ 1 本（種別と両端の番号・起点の表示名）
 * @graph-connects none
 */
export const ElementDanglingGraphReferenceRowSchema = z.object({
    edgeType: SpecEdgeTypeSchema,
    sourceId: SpecElementIdSchema,
    sourceLabel: z.string(),
    untranslated: UntranslatedSchema,
    targetId: SpecElementIdSchema,
});
/**
 * どこからも参照されない用語 1 件。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business どこからも参照されない用語 1 件（用語の番号と表示名）
 * @graph-connects none
 */
export const ElementOrphanTermRowSchema = z.object({
    termId: SpecElementIdSchema,
    label: z.string(),
    untranslated: UntranslatedSchema,
});
/**
 * 出力。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 作り直しの宙吊り参照監査の出力（宙吊りの処理・宙吊りのエッジ・孤立した用語）
 * @graph-connects none
 */
export const GetElementDanglingReferencesOutputSchema = z.object({
    generatedAt: IsoDateTimeSchema,
    danglingImplementation: z.array(ElementDanglingImplementationRowSchema),
    danglingGraphReference: z.array(ElementDanglingGraphReferenceRowSchema),
    orphanTerm: z.array(ElementOrphanTermRowSchema),
});
//# sourceMappingURL=spec-graph-element-audit.js.map